import {
  Content as RadixContent,
  Portal as RadixPortal,
} from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';

import { ChevronLeftSmallIcon } from '@dynatrace/strato-icons';

import { MobileMenuNavigationContext } from './contexts/MobileMenuNavigationContext.js';
import { getSubTriggerSlots } from './item-slots.js';
import * as styles from './MobileMenu.sty.js';
import { type MenuContentProps } from './types.js';
import { useFocusRing } from '../../core/hooks/useFocusRing.js';
import { useOverlayStacking } from '../../core/hooks/useOverlayStacking.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';

type InternalMobileContentProps = MenuContentProps & {
  _sideOffset?: number;
  _contentId?: string;
};

const SIDE_OFFSET = 4;

/**
 * @internal
 */
export const MobileContent = (props: InternalMobileContentProps) => {
  const {
    children,
    side = 'bottom',
    alignment = 'start',
    onCloseAutoFocus,
    avoidCollisions = true,
    collisionBoundary,
    _sideOffset = SIDE_OFFSET,
    _contentId,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
  } = props as InternalMobileContentProps;

  const intl = useIntl();
  const { stack, direction, pop } = useContext(MobileMenuNavigationContext);
  const { style: overlayStyle } = useOverlayStacking(undefined, 'overlay');
  const { focusProps: backFocusProps, focusClassName: backFocusClassName } =
    useFocusRing();

  const panelRef = useRef<HTMLDivElement>(null);
  const [panelWidth, setPanelWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (panelRef.current && panelWidth === undefined) {
      setPanelWidth(panelRef.current.offsetWidth);
    }
  }, [panelWidth]);

  const currentLevel = stack.length > 0 ? stack[stack.length - 1] : null;
  const currentContent: ReactNode = currentLevel
    ? currentLevel.content
    : children;
  const titleSlots = currentLevel
    ? getSubTriggerSlots(currentLevel.title)
    : null;

  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (stack.length > 0) {
        pop();
        e.preventDefault();
      }
      e.stopPropagation();
    },
    [pop, stack.length],
  );

  return (
    <RadixPortal>
      <RadixContent
        ref={panelRef}
        id={_contentId}
        side={side}
        align={alignment}
        sideOffset={_sideOffset}
        avoidCollisions={avoidCollisions}
        collisionBoundary={collisionBoundary}
        onCloseAutoFocus={onCloseAutoFocus}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        className={styles.panel}
        style={{ zIndex: overlayStyle.zIndex, width: panelWidth }}
        onEscapeKeyDown={handleEscapeKeyDown}
      >
        {currentLevel && (
          <div className={styles.header}>
            <button
              className={clsx(styles.backButton, backFocusClassName)}
              onClick={pop}
              aria-label={intl.formatMessage({
                id: 'tSs2tyafv8WOwku3',
                defaultMessage: 'Back',
              })}
              type="button"
              role="menuitem"
              {...backFocusProps}
            >
              <ChevronLeftSmallIcon />
              <div className={styles.headerTitleWrapper}>
                {titleSlots?.prefix}
                <span
                  className={clsx(
                    textStyleCSS({ textStyle: 'base-emphasized' }),
                    styles.headerTitleText,
                  )}
                >
                  {titleSlots?.freeform}
                </span>
              </div>
            </button>
          </div>
        )}
        <div
          className={clsx(styles.body, {
            [styles.bodyAnimatedForward]:
              direction === 'forward' && stack.length > 0,
            [styles.bodyAnimatedBack]: direction === 'back',
          })}
          key={stack.length}
        >
          {currentContent}
        </div>
      </RadixContent>
    </RadixPortal>
  );
};

(MobileContent as typeof MobileContent & { displayName: string }).displayName =
  'Menu.Content';
